<!-- Responsibility: Implement the "Negotiation Table" spatial metaphor for strategic trading.
Scope: Trade construction, value appraisal, and leverage-based proposal logic. -->
<trade-dialog>
  <div class="trade-root">
    
    <!-- Active Negotiation Surface -->
    <main class="negotiation-table">
      <!-- Player Side -->
      <article class="stake player-side">
        <header>
          <h3>YOUR STAKE</h3>
          <span class="total">{ state.playerWorth }{ state.economy?.currencySymbol || '' }</span>
        </header>
        <ul class="row-list" onclick={ onPlayerStakeClick }>
          <li each={ item in state.playerOffer } class="trade-row active">
            <button class="item-id" data-item={ item.name }>
              <object-visual name={ item.name } size="sm" icon-only={true}></object-visual>
              <div class="meta">
                <span class="label">{ item.name }</span>
                <div class="scarcity" data-level={ getScarcity(item.worth) }>
                  <span></span><span></span><span></span>
                </div>
              </div>
            </button>
            <div class="worth">{ item.worth }{ state.economy?.currencySymbol }</div>
            <button class="action remove" data-remove-item={ item.name }>REMOVE</button>
          </li>
          <li class="empty-hint" if={ !state.playerOffer.length }>
            <span>PUSH ITEMS FROM SATCHEL</span>
          </li>
        </ul>
      </article>

      <!-- The Balance Pivot -->
      <aside class="value-pivot" aria-label="Negotiation Balance">
        <div class="track">
          <div class="gauge { getBalanceClass() }" style="height: { getPivotHeight() }%; bottom: { getPivotBottom() }%"></div>
          <div class="dead-center"></div>
        </div>
        <div class="status">{ getLeverageLabel() }</div>
      </aside>

      <!-- NPC Side -->
      <article class="stake npc-side">
        <header>
          <h3>{ state.npcName.toUpperCase() }'S STAKE</h3>
          <span class="total">{ state.npcWorth }{ state.economy?.currencySymbol || '' }</span>
        </header>
        <ul class="row-list" onclick={ onNpcStakeClick }>
          <virtual if={ state.partnerInventory.length }>
            <li each={ item in state.partnerInventory } class="trade-row { isNpcSelected(item.name) ? 'active' : '' }">
              <button class="item-id" data-item={ item.name }>
                <object-visual name={ item.name } size="sm" icon-only={true}></object-visual>
                <div class="meta">
                  <span class="label">{ item.name }</span>
                  <div class="scarcity" data-level={ getScarcity(item.worth) }>
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </button>
              <div class="worth">{ item.worth }{ state.economy?.currencySymbol }</div>
              <button class="action { isNpcSelected(item.name) ? 'remove' : 'add' }" data-npc-toggle-item={ item.name }>
                { isNpcSelected(item.name) ? 'DROP' : 'ASK' }
              </button>
            </li>
          </virtual>

          <li if={ !state.partnerInventory.length && state.npcOffer } class="trade-row active">
            <button class="item-id" data-item={ state.npcOffer.name }>
              <object-visual name={ state.npcOffer.name } size="sm" icon-only={true}></object-visual>
              <div class="meta">
                <span class="label">{ state.npcOffer.name }</span>
                <div class="scarcity" data-level={ getScarcity(state.npcOffer.worth) }>
                  <span></span><span></span><span></span>
                </div>
              </div>
            </button>
            <div class="worth">{ state.npcOffer.worth }{ state.economy?.currencySymbol }</div>
            <div class="action static">OFFERED</div>
          </li>

          <li class="empty-hint" if={ !state.partnerInventory.length && !state.npcOffer }>
            <span>{ state.loadingOffer ? 'APPRAISING...' : 'AWAITING PROPOSAL' }</span>
          </li>
        </ul>
      </article>
    </main>

    <!-- Selection Surface -->
    <section class="satchel">
      <header>
        <h3>YOUR SATCHEL</h3>
        <div class="econ-context" if={ state.economy }>
          { state.economy.currencyName } · 1 Ration = { state.economy.rationCost }{ state.economy.currencySymbol }
        </div>
      </header>
      
      <ul class="row-list satchel-scroll" onclick={ onSatchelClick }>
        <li each={ item in state.inventory } class="trade-row { isInOffer(item.name) ? 'staked' : '' }">
          <button class="item-id" data-item={ item.name }>
            <object-visual name={ item.name } size="sm" icon-only={true}></object-visual>
            <div class="meta">
              <span class="label">{ item.name }</span>
              <div class="scarcity" data-level={ getScarcity(item.worth) }>
                <span></span><span></span><span></span>
              </div>
            </div>
          </button>
          <div class="worth">{ item.worth || '-' }{ state.economy?.currencySymbol || '' }</div>
          <button class="action add" data-offer-item={ item.name }>
            { isInOffer(item.name) ? 'ON TABLE' : 'PUSH TO TABLE' }
          </button>
        </li>
        <li class="empty-msg" if={ !state.inventory.length }>Satchel is empty.</li>
      </ul>
    </section>

    <!-- Control Surface -->
    <footer class="trade-controls">
      <button class="btn-secondary" onclick={ closeDialog }>LEAVE TABLE</button>
      <div class="leverage-group">
        <div class="leverage-readout { getBalanceClass() }">
          <small>LEVERAGE:</small>
          <strong>{ getLeverageLabel() }</strong>
        </div>
        <button
          class="btn-primary { canPropose() ? 'active' : '' }"
          onclick={ propose }
          disabled={ !canPropose() }
        >
          <span class="main">PROPOSE DEAL</span>
          <span class="sub">Uses Persuasion Check</span>
        </button>
      </div>
    </footer>
  </div>

  <script>
    import { dispatcher } from '../../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../../game-store.js'
    import { getNpcTradeSeed } from '../../../ai/logicForge.js'
    import { buildActionTarget, buildToolRef } from '../../helpers/action-refs.ts'
    import { openItemDialog } from '../../dialog/dialog-openers.ts'
    import ObjectVisual from '../object-visual.riot'

    export default {
      components: { ObjectVisual },

      state: {
        playerOffer: [],
        npcOffer: null,
        npcSelected: [],
        partnerInventory: [],
        inventory: [],
        loadingOffer: false,
        openToken: 0,
        npcName: 'Trader',
        economy: null,
        playerWorth: 0,
        npcWorth: 0,
      },

      onMounted() {
        this.syncForOpen(this.props)
      },

      onBeforeUpdate(props) {
        const nextToken = Number(props?.openToken ?? 0)
        if (nextToken !== Number(this.state.openToken ?? 0)) {
          this.syncForOpen(props)
        }
      },

      async syncForOpen(props) {
        const nextToken = Number(props?.openToken ?? 0)
        const content = props.content ?? {}
        const npc = content.npc ?? null
        const economy = gameStore.state?.world.taxonomy?.economy ?? null
        
        // Restore from gameStore negotiation state (PERSISTENCE)
        const partner = Array.isArray(content.partnerInventory) ? content.partnerInventory : []
        const inventory = Array.isArray(content.inventory) ? content.inventory : []

        this.update({
          playerOffer: gameStore.negotiation.playerOffer,
          npcOffer: gameStore.negotiation.npcOffer,
          npcSelected: gameStore.negotiation.npcSelected,
          partnerInventory: partner,
          inventory,
          loadingOffer: !partner.length && !!npc && !gameStore.negotiation.npcOffer,
          openToken: nextToken,
          npcName: npc?.name || 'Trader',
          economy,
          playerWorth: gameStore.negotiation.playerOffer.reduce((s, i) => s + (i.worth || 0), 0),
          npcWorth: (gameStore.negotiation.npcOffer?.worth || 0) + gameStore.negotiation.npcSelected.reduce((s, i) => s + (i.worth || 0), 0),
        })

        if (partner.length || !npc || gameStore.negotiation.npcOffer) {
          this.update({ loadingOffer: false })
          return
        }

        try {
          const seed = await getNpcTradeSeed(npc, gameStore.state)
          if (Number(this.state.openToken ?? 0) !== nextToken) return
          const npcOffer = typeof seed === 'string' ? { name: seed, worth: 0 } : { name: seed.offer, worth: seed.worth }
          gameStore.negotiation.npcOffer = npcOffer
          this.update({ npcOffer, loadingOffer: false, npcWorth: npcOffer.worth || 0 })
        } catch (error) {
          console.error('Trade seed failed:', this.summarizeError(error))
          if (Number(this.state.openToken ?? 0) !== nextToken) return
          this.update({ loadingOffer: false })
        }
      },

      summarizeError(error) {
        const raw = error instanceof Error
          ? `${error.name}: ${error.message}`
          : String(error ?? 'unknown error')
        const compact = raw.replace(/\s+/g, ' ').trim()
        return compact.length > 180 ? `${compact.slice(0, 177)}...` : compact
      },

      async closeDialog() {
        gameStore.resetNegotiation()
        dispatcher.trigger('trade-dialog', TOPICS.trade.close)
        await this.props.requestModalClose?.('cancel')
      },

      inspectItem(name) {
        openItemDialog('trade-dialog', name)
      },

      getScarcity(worth) {
        const cost = this.state.economy?.rationCost || 10
        if (!worth) return 1
        if (worth < cost * 2) return 1
        if (worth < cost * 100) return 2
        return 3
      },

      isInOffer(name) { return this.state.playerOffer.some(i => i.name === name) },
      isNpcSelected(name) { return this.state.npcSelected.some(i => i.name === name) },

      toggleOffer(item) {
        const offer = this.state.playerOffer
        const exists = offer.find(i => i.name === item.name)
        const nextOffer = exists ? offer.filter(i => i.name !== item.name) : [...offer, item]
        gameStore.negotiation.playerOffer = nextOffer
        this.update({ playerOffer: nextOffer, playerWorth: nextOffer.reduce((s, i) => s + (i.worth || 0), 0) })
      },

      toggleNpcSelected(item) {
        const sel = this.state.npcSelected
        const exists = sel.find(i => i.name === item.name)
        const nextSel = exists ? sel.filter(i => i.name !== item.name) : [...sel, item]
        gameStore.negotiation.npcSelected = nextSel
        this.update({ npcSelected: nextSel, npcWorth: (this.state.npcOffer?.worth || 0) + nextSel.reduce((s, i) => s + (i.worth || 0), 0) })
      },

      removeFromOffer(item) {
        const playerOffer = this.state.playerOffer.filter(i => i.name !== item.name)
        gameStore.negotiation.playerOffer = playerOffer
        this.update({ playerOffer, playerWorth: playerOffer.reduce((s, i) => s + (i.worth || 0), 0) })
      },

      findItemByName(list, name) {
        return (list ?? []).find(entry => entry?.name === name) ?? null
      },

      onPlayerStakeClick(event) {
        const button = event?.target?.closest?.('button')
        if (!button) return
        const itemName = String(button.dataset.item ?? button.dataset.removeItem ?? '').trim()
        if (!itemName) return
        if (button.dataset.item) {
          this.inspectItem(itemName)
          return
        }
        if (button.dataset.removeItem) {
          const item = this.findItemByName(this.state.playerOffer, itemName)
          if (item) this.removeFromOffer(item)
        }
      },

      onNpcStakeClick(event) {
        const button = event?.target?.closest?.('button')
        if (!button) return
        const itemName = String(button.dataset.item ?? button.dataset.npcToggleItem ?? '').trim()
        if (!itemName) return
        if (button.dataset.item) {
          this.inspectItem(itemName)
          return
        }
        if (button.dataset.npcToggleItem) {
          const item = this.findItemByName(this.state.partnerInventory, itemName)
          if (item) this.toggleNpcSelected(item)
        }
      },

      onSatchelClick(event) {
        const button = event?.target?.closest?.('button')
        if (!button) return
        const itemName = String(button.dataset.item ?? button.dataset.offerItem ?? '').trim()
        if (!itemName) return
        if (button.dataset.item) {
          this.inspectItem(itemName)
          return
        }
        if (button.dataset.offerItem && !this.isInOffer(itemName)) {
          const item = this.findItemByName(this.state.inventory, itemName)
          if (item) this.toggleOffer(item)
        }
      },

      canPropose() { return (this.state.playerOffer.length > 0 || this.state.npcSelected.length > 0 || !!this.state.npcOffer) && !this.state.loadingOffer },

      getBalanceClass() {
        const diff = this.state.playerWorth - this.state.npcWorth
        const threshold = (this.state.economy?.rationCost || 10) / 2
        if (Math.abs(diff) < threshold) return 'neutral'
        return diff > 0 ? 'over' : 'under'
      },

      getLeverageLabel() {
        const diff = this.state.playerWorth - this.state.npcWorth
        const cost = this.state.economy?.rationCost || 10
        if (Math.abs(diff) < cost / 2) return 'FAIR DEAL'
        if (diff > cost * 5) return 'GENEROUS'
        if (diff > 0) return 'FAVORABLE'
        if (diff < -(cost * 10)) return 'EXTREME'
        if (diff < -(cost * 3)) return 'RISKY'
        return 'TOUGH'
      },

      getPivotHeight() {
        const total = this.state.playerWorth + this.state.npcWorth
        if (total === 0) return 0
        const diff = Math.abs(this.state.playerWorth - this.state.npcWorth)
        const max = Math.max(this.state.playerWorth, this.state.npcWorth, 1)
        return Math.min(100, (diff / max) * 100)
      },

      getPivotBottom() { return this.state.playerWorth >= this.state.npcWorth ? 50 : Math.max(0, 50 - this.getPivotHeight()) },

      async propose() {
        const playerItems = this.state.playerOffer.map(i => i.name)
        const npcName = this.state.npcName
        const receiveItems = this.state.partnerInventory.length > 0 ? this.state.npcSelected.map(i => i.name) : (this.state.npcOffer ? [this.state.npcOffer.name] : [])
        const diff = this.state.playerWorth - this.state.npcWorth
        const balanceDesc = diff > 0 ? `generous by ${diff}` : (diff < 0 ? `demanding by ${Math.abs(diff)}` : 'fair')
        const sentence = `Trade: I give ${playerItems.join(', ') || 'nothing'} to ${npcName} and ask for ${receiveItems.join(', ') || 'nothing'} in return. [Economic balance: ${balanceDesc} ${this.state.economy?.currencySymbol || ''}]`
        
        gameStore.resetNegotiation()
        await this.props.requestModalClose?.('submit')
        dispatcher.trigger('trade-dialog', TOPICS.trade.proposed, { offer: playerItems, receive: receiveItems, playerWorth: this.state.playerWorth, npcWorth: this.state.npcWorth })
        gameStore.handleAction(sentence, undefined, {
          source: 'player',
          category: 'interact',
          tool: buildToolRef('free-text', 'trade', 'Trade'),
          explicitTargets: npcName ? [buildActionTarget('npc', npcName)] : [],
          tags: ['trade_dialog', 'trade_propose'],
        })
      },
    }
  </script>

  <style>
    .trade-root {
      display: flex;
      flex-direction: column;
      gap: var(--space-md);
      color: var(--text-prose);
      height: 100%;
    }

    /* Common List resets */
    .row-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    /* Negotiation Table (Top) */
    .negotiation-table {
      display: grid;
      grid-template-columns: 1fr 3rem 1fr;
      gap: var(--space-md);
      background: var(--surface-card-1);
      padding: var(--space-md);
      border-radius: 1rem;
      border: 1px solid var(--border);
      min-height: 18rem;
    }

    .stake {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
      min-width: 0;

      header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        border-bottom: 1px solid var(--border-subtle);
        padding-bottom: 0.25rem;
      }

      h3 {
        font-size: 0.65rem;
        font-weight: 800;
        letter-spacing: 0.12em;
        color: var(--text-dim);
        margin: 0;
      }

      .total {
        font-size: 0.875rem;
        font-weight: 900;
        color: var(--text-prose);
      }
    }

    .player-side h3 { color: var(--brand-primary); }
    .npc-side h3 { color: var(--gold); }

    /* The Row Component */
    .trade-row {
      display: grid;
      grid-template-columns: 1fr auto min-content;
      align-items: center;
      gap: var(--space-sm);
      background: var(--surface-card-3);
      border: 1px solid var(--border);
      padding: 0.4rem 0.75rem;
      border-radius: 0.5rem;
      transition: all 0.16s ease;

      &.active {
        border-color: var(--alpha-mid);
        background: var(--bg);
      }

      &.staked {
        opacity: 0.2;
        filter: grayscale(1);
        pointer-events: none;
      }

      .item-id {
        background: none;
        border: none;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        cursor: pointer;
        padding: 0;
        text-align: left;
        min-width: 0;
        color: inherit;
        font-family: inherit;

        .meta {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .label {
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .worth {
        font-size: 0.7rem;
        font-weight: 900;
        color: var(--gold);
        text-align: right;
        min-width: 3rem;
      }

      .action {
        background: var(--bg);
        border: 1px solid var(--border);
        color: var(--text-muted);
        font-size: 0.55rem;
        font-weight: 800;
        padding: 0.35rem 0.5rem;
        border-radius: 0.25rem;
        cursor: pointer;
        white-space: nowrap;

        &.add { color: var(--sem-success-fg); border-color: var(--sem-success-border); }
        &.remove { color: var(--sem-danger-fg); border-color: var(--sem-danger-border); }
        &.static { border: none; cursor: default; opacity: 0.6; }

        &:hover:not(.static) {
          background: var(--surface-card-1);
          border-color: var(--gold);
        }
      }
    }

    .empty-hint {
      flex: 1;
      border: 1px dashed var(--border);
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--alpha-ghost);
      font-size: 0.6rem;
      font-weight: 800;
      color: var(--text-dim);
      opacity: 0.6;
      min-height: 4rem;
    }

    /* Pivot Visual */
    .value-pivot {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;

      .track {
        width: 0.5rem;
        flex: 1;
        background: var(--bg-darker);
        border-radius: 1rem;
        border: 1px solid var(--border);
        position: relative;
        overflow: hidden;
      }

      .dead-center {
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 1px;
        background: var(--text-muted);
        z-index: 2;
      }

      .gauge {
        position: absolute;
        left: 0;
        right: 0;
        transition:
          height 0.35s ease,
          bottom 0.35s ease,
          background-color 0.2s ease,
          box-shadow 0.24s ease;
        will-change: height, bottom;
        border-radius: 1rem;

        &.neutral { background: var(--text-dim); }
        &.over { background: var(--brand-primary); box-shadow: 0 0 1rem var(--brand-primary-glow); }
        &.under { background: var(--gold); box-shadow: 0 0 1rem var(--gold-glow); }
      }

      .status {
        writing-mode: vertical-lr;
        font-size: 0.55rem;
        font-weight: 900;
        color: var(--text-dim);
        transform: rotate(180deg);
        white-space: nowrap;
      }
    }

    /* Satchel (Bottom) */
    .satchel {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
      background: var(--bg-darker);
      border: 1px solid var(--border);
      border-radius: 1rem;
      padding: var(--space-md);

      header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: var(--space-sm);
        border-radius: 0.75rem;
        padding: 0.35rem 0.5rem;
        transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

        &:hover {
          background: color-mix(in srgb, var(--surface-card-2) var(--alpha-low), transparent);
          box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--gold) var(--alpha-low), transparent);
          transform: translateY(-1px);
        }
      }

      h3 {
        font-size: 0.65rem;
        font-weight: 800;
        color: var(--text-muted);
        margin: 0;
      }

      .econ-context {
        font-size: 0.6rem;
        color: var(--text-dim);
      }

      .satchel-scroll {
        max-height: 15rem;
        overflow-y: auto;
        padding-right: 0.5rem;
      }
    }

    /* Scarcity Visual */
    .scarcity {
      display: flex;
      gap: 0.125rem;
      margin-top: 0.125rem;

      span {
        width: 0.5rem;
        height: 0.125rem;
        background: var(--border);
        border-radius: 1px;
      }

      &[data-level="1"] span:nth-child(1) { background: var(--text-muted); }
      &[data-level="2"] span:nth-child(-n+2) { background: var(--brand-primary); }
      &[data-level="3"] span:nth-child(-n+3) { background: var(--gold); }
    }

    /* Controls (Footer) */
    .trade-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: var(--space-md);
      border-top: 1px solid var(--border);
    }

    .btn-secondary {
      background: none;
      border: 1px solid var(--border);
      color: var(--text-muted);
      font-size: 0.65rem;
      font-weight: 800;
      padding: 0.6rem 1rem;
      border-radius: 0.4rem;
      cursor: pointer;
    }

    .leverage-group {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .leverage-readout {
      display: flex;
      flex-direction: column;
      align-items: flex-end;

      small { font-size: 0.5rem; font-weight: 800; color: var(--text-dim); }
      strong { font-size: 0.75rem; font-weight: 900; }

      &.over strong { color: var(--sem-success-fg); }
      &.under strong { color: var(--sem-warn-fg); }
    }

    .btn-primary {
      background: var(--surface-card-2);
      border: 1px solid var(--border);
      color: var(--text-dim);
      padding: 0.6rem 1.5rem;
      border-radius: 0.5rem;
      cursor: not-allowed;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 12rem;

      &.active {
        background: var(--gold);
        border-color: var(--gold);
        color: black;
        cursor: pointer;
        box-shadow: 0 0 1rem rgba(245, 183, 49, 0.25);
      }

      .main { font-size: 0.75rem; font-weight: 900; }
      .sub { font-size: 0.5rem; font-weight: 700; opacity: 0.8; }
    }

    /* Mobile Adaptations */
    @media (max-width: 48rem) {
      .negotiation-table {
        grid-template-columns: 1fr;
        grid-template-rows: auto 3rem auto;
        min-height: auto;
      }
      .value-pivot {
        flex-direction: row;
        height: 3rem;

        .track { width: 100%; height: 0.5rem; }
        .dead-center { top: 0; bottom: 0; left: 50%; width: 1px; height: auto; }
        .status { writing-mode: horizontal-tb; transform: none; }
      }
    }
  </style>
</trade-dialog>
